// pylee/apps/web/app/pricing/page.tsx
'use client';

import { useState } from 'react';
import { Check, X, Zap, Shield, Users, ArrowRight, Star, Award } from 'lucide-react';
import styles from './pricing.module.css';
import CTA from '@/app/(home)/components/cta';

// Define plan feature data structure
interface PlanFeature {
  name: string;
  free: string | boolean;
  pro: string | boolean;
  team: string | boolean;
  tooltip?: string;
}

const Pricing = () => {
  // State for annual vs monthly billing
  const [isAnnual, setIsAnnual] = useState(true);
  
  // Plan features data
  const planFeatures: PlanFeature[] = [
    {
      name: 'Registry management',
      free: '1 private',
      pro: '3+ private',
      team: 'Unlimited',
      tooltip: 'Number of private registries you can create and manage'
    },
    {
      name: 'Gateway Functionality',
      free: 'Basic',
      pro: 'Enhanced',
      team: 'Advanced',
      tooltip: 'Security and routing capabilities of your gateway'
    },
    {
      name: 'Access Controls',
      free: 'Self',
      pro: 'Basic',
      team: 'Team-level',
      tooltip: 'Granular permission settings for your AI tools'
    },
    {
      name: 'API Call Limits',
      free: '10k/mo',
      pro: '100k/mo',
      team: '1M/user/mo',
      tooltip: 'Monthly API request limits per account'
    },
    {
      name: 'User Support',
      free: 'Community',
      pro: 'Email',
      team: 'Priority',
      tooltip: 'Available support channels and response times'
    },
    {
      name: 'Audit & Governance',
      free: 'None',
      pro: 'Basic',
      team: 'Basic Audit',
      tooltip: 'Tracking and compliance features'
    },
    {
      name: 'Deployment Options',
      free: 'Hosted Only',
      pro: 'Hosted Only',
      team: 'Hosted Only',
      tooltip: 'Where your Pylee instance can be deployed'
    },
  ];

  // Return whether a feature is included or not
  const renderFeature = (value: string | boolean) => {
    if (value === true) {
      return <Check className={styles.checkIcon} size={20} />;
    } else if (value === false) {
      return <X className={styles.xIcon} size={20} />;
    } else {
      return value;
    }
  };

  // Team plan pricing variables
  const perSeatMonthly = 24;
  const perSeatAnnual = 19;
  const minSeats = 5;
  const proMonthly = 39;
  const proAnnual = 29;
  
  // Calculate team savings per seat vs Pro
  const teamSavingPercent = Math.round(((isAnnual ? proAnnual : proMonthly) - (isAnnual ? perSeatAnnual : perSeatMonthly)) / (isAnnual ? proAnnual : proMonthly) * 100);
  const teamMinPrice = (isAnnual ? perSeatAnnual : perSeatMonthly) * minSeats;

  return (
    <>
      <section className={`content-section ${styles.pricingSection}`}>
        <div className="page-container">
          <h1 className="section-heading">
            Simple, Transparent <span>Pricing</span>
          </h1>
          <p className="section-subheading">
            Start with the free tier and scale as your AI toolkit grows.
            No hidden fees or surprises.
          </p>

          {/* Billing Toggle */}
          <div className={styles.billingToggle}>
            <span className={!isAnnual ? styles.activeBilling : ''}>Monthly</span>
            <button 
              className={styles.toggleSwitch} 
              onClick={() => setIsAnnual(!isAnnual)}
              aria-checked={isAnnual}
              role="switch"
            >
              <span className={styles.toggleKnob}></span>
            </button>
            <span className={isAnnual ? styles.activeBilling : ''}>
              Annual
              <span className={styles.saveBadge}>Save 20%</span>
            </span>
          </div>

          {/* Pricing Cards */}
          <div className={styles.pricingGrid}>
            {/* Free Plan */}
            <div className={`${styles.pricingCard} ${styles.freePlan}`}>
              <div className={styles.planHeader}>
                <div className={styles.planIcon}>
                  <Zap size={24} />
                </div>
                <h3>Free</h3>
                <div className={styles.planPrice}>
                  <span className={styles.currency}>$</span>
                  <span className={styles.amount}>0</span>
                  <span className={styles.period}>/month</span>
                </div>
                <p className={styles.planSubtitle}>For individuals getting started with AI tools</p>
              </div>
              <div className={styles.planFeatures}>
                <ul>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>1 private registry</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Basic gateway functionality</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>10,000 API calls per month</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Community support</span>
                  </li>
                </ul>
              </div>
              <a href="https://app.pyleeai.com/" className={styles.planButton}>
  Get Started <ArrowRight size={16} className={styles.buttonIcon} />
</a>
            </div>

            {/* Pro Plan */}
            <div className={`${styles.pricingCard} ${styles.proPlan}`}>
              <div className={styles.popularTag}>Most Popular</div>
              <div className={styles.planHeader}>
                <div className={styles.planIcon}>
                  <Star size={24} />
                </div>
                <h3>Pro</h3>
                <div className={styles.planPrice}>
                  <span className={styles.currency}>$</span>
                  <span className={styles.amount}>{isAnnual ? '29' : '39'}</span>
                  <span className={styles.period}>/month</span>
                </div>
                <p className={styles.planSubtitle}>For professionals and solo developers</p>
              </div>
              <div className={styles.planFeatures}>
                <ul>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>3+ private registries</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Enhanced gateway with improved security</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Basic access controls</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>100,000 API calls per month</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Email support</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Basic audit & governance</span>
                  </li>
                </ul>
              </div>
              <a href="#cta" className={`${styles.planButton} ${styles.primaryPlanButton}`}>
                Upgrade to Pro <ArrowRight size={16} className={styles.buttonIcon} />
              </a>
            </div>

            {/* Team Plan */}
            <div className={`${styles.pricingCard} ${styles.teamPlan}`}>
              <div className={styles.bestValueTag}>Best Value</div>
              <div className={styles.planHeader}>
                <div className={styles.planIcon}>
                  <Users size={24} />
                </div>
                <h3>Team</h3>
                <div className={styles.planPrice}>
                  <span className={styles.currency}>$</span>
                  <span className={styles.amount}>{isAnnual ? perSeatAnnual : perSeatMonthly}</span>
                  <span className={styles.period}>/seat/month</span>
                </div>
                <div className={styles.minSeatsNote}>
                  <span>{minSeats} seats minimum</span>
                  <span className={styles.teamSavingsBadge}>Save {teamSavingPercent}% per seat vs Pro</span>
                </div>
                <p className={styles.planSubtitle}>For organizations and growing teams</p>
              </div>
              <div className={styles.planFeatures}>
                <ul>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Unlimited private registries</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Advanced gateway functionality</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Team-level access controls</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>1M API calls per user/month</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Priority support</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Advanced audit capabilities</span>
                  </li>
                  <li>
                    <Check size={16} className={styles.featureIcon} />
                    <span>Centralized team billing & admin</span>
                  </li>
                </ul>
              </div>
              <a href="#cta" className={`${styles.planButton} ${styles.teamPlanButton}`}>
                Get Team Pricing <ArrowRight size={16} className={styles.buttonIcon} />
              </a>
              <div className={styles.totalPriceNote}>
                Starting at ${teamMinPrice}/month total
              </div>
            </div>
          </div>

          {/* Enterprise Section */}
          <div className={styles.enterpriseSection}>
            <div className={styles.enterpriseContent}>
              <h3>Need a custom solution?</h3>
              <p>For large organizations with specific requirements, we offer tailored solutions including on-premises deployment, custom SLAs, and dedicated support.</p>
              <a href="#cta" className={`btn btn-primary ${styles.enterpriseButton}`}>
                Talk to an Expert
              </a>
            </div>
            <div className={styles.enterpriseFeatures}>
              <div className={styles.enterpriseFeature}>
                <Shield size={20} className={styles.enterpriseIcon} />
                <h4>Security & Compliance</h4>
                <p>On-premises deployment with air-gapped options and custom security protocols</p>
              </div>
              <div className={styles.enterpriseFeature}>
                <Users size={20} className={styles.enterpriseIcon} />
                <h4>Dedicated Support</h4>
                <p>Dedicated success manager and implementation specialists</p>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className={styles.comparisonSection}>
            <h3 className={styles.comparisonTitle}>Compare All Features</h3>
            
            <div className={styles.comparisonTable}>
              <div className={styles.tableHeader}>
                <div className={styles.featureColumn}>Feature</div>
                <div className={styles.planColumn}>Free</div>
                <div className={styles.planColumn}>Pro</div>
                <div className={`${styles.planColumn} ${styles.highlightColumn}`}>Team</div>
              </div>
              
              {planFeatures.map((feature, idx) => (
                <div key={idx} className={styles.tableRow}>
                  <div className={styles.featureColumn}>
                    {feature.name}
                    {feature.tooltip && (
                      <span className={styles.featureTooltip} title={feature.tooltip}>?</span>
                    )}
                  </div>
                  <div className={styles.planColumn}>{renderFeature(feature.free)}</div>
                  <div className={styles.planColumn}>{renderFeature(feature.pro)}</div>
                  <div className={`${styles.planColumn} ${styles.highlightColumn}`}>{renderFeature(feature.team)}</div>
                </div>
              ))}
              <div className={styles.tableRow}>
                <div className={styles.featureColumn}>
                  Team Management
                  <span className={styles.featureTooltip} title="Central user management, roles and permissions">?</span>
                </div>
                <div className={styles.planColumn}><X className={styles.xIcon} size={20} /></div>
                <div className={styles.planColumn}><X className={styles.xIcon} size={20} /></div>
                <div className={`${styles.planColumn} ${styles.highlightColumn}`}><Check className={styles.checkIcon} size={20} /></div>
              </div>
              <div className={styles.tableRow}>
                <div className={styles.featureColumn}>
                  Cost Per Seat
                </div>
                <div className={styles.planColumn}>$0</div>
                <div className={styles.planColumn}>${isAnnual ? proAnnual : proMonthly}</div>
                <div className={`${styles.planColumn} ${styles.highlightColumn}`}>
                  ${isAnnual ? perSeatAnnual : perSeatMonthly} 
                  <span className={styles.tableComparisonSavings}>({teamSavingPercent}% savings)</span>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className={styles.faqSection}>
            <h3 className={styles.faqTitle}>Frequently Asked Questions</h3>
            <div className={styles.faqGrid}>
              <div className={styles.faqItem}>
                <h4>Can I upgrade or downgrade my plan later?</h4>
                <p>Yes, you can change your plan at any time. When upgrading, you'll be prorated for the remainder of your billing period. When downgrading, changes take effect at the end of your current billing period.</p>
              </div>
              <div className={styles.faqItem}>
                <h4>How does Team pricing work?</h4>
                <p>Team pricing is charged per user with a minimum of {minSeats} seats. Each user gets their own account while benefiting from centralized management, shared resources, and enhanced collaboration features.</p>
              </div>
              <div className={styles.faqItem}>
                <h4>How do API call limits work?</h4>
                <p>API calls are counted when your applications interact with our platform. Free tier limits reset monthly. On the Team plan, each user gets their own API call quota, making it more scalable for growing teams.</p>
              </div>
              <div className={styles.faqItem}>
                <h4>Do you offer educational or non-profit discounts?</h4>
                <p>Yes, we offer special pricing for educational institutions and qualified non-profit organizations. Please contact our sales team for more information.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section - now outside the pricing section */}
      <CTA />
    </>
  );
};

export default Pricing;